import { call, fork, put, takeEvery } from 'redux-saga/effects';
import { replace, push } from 'react-router-redux';
import { message } from 'antd';

import * as ga from '../redux/actions/app';
import {
  FETCH_USERS_MODULES_REQUEST,
  fetchUsersModulesFail,
  fetchUsersModulesRequest,
  fetchUsersModulesSuccess,
  POST_LOGIN_REQUEST,
  POST_LOGOUT_REQUEST,
  postLoginFail,
  postLoginSuccess,
  postLogoutDone,
  PRODUCT_LOGIN_REQUEST,
  productLoginFail,
  productLoginSuccess,
  PUT_CHANGE_PASSWORD_REQUEST,
  putChangePasswordFail,
  putChangePasswordSuccess,
  PUT_CHANGE_DEFAULT_ROLE_REQUEST,
  putChangeDefaultRoleFail,
  putChangeDefaultRoleSuccess,
  resetLogin,
  setCurrentProductLogin
} from '../redux/actions/authentication';
import {
  eraseCookie,
  fetchResources,
  fetchUsersModules,
  postLogin,
  postLogout,
  putChangeDefaultRole,
  putChangePassword,
  setCookie
} from '../services/authentication';
import { COOKIE_NAME } from '../util/GlobalConstant';
import { sortProductsOptions, filterRole } from '../services/util';

function* fetchUsersModulesWorker() {
  try {
    const token = localStorage.getItem('id_token');
    const usersModulesResponse = yield call(fetchUsersModules, token);

    if (usersModulesResponse.status === 200 && usersModulesResponse.data) {
      const modules = sortProductsOptions(usersModulesResponse.data || []);
      const usersModules = filterRole(modules);
      yield put(fetchUsersModulesSuccess(usersModules));

      return usersModules;
    } else {
      throw usersModulesResponse;
    }
  } catch (error) {
    yield put(fetchUsersModulesFail(error));
    return [];
  }
}

function* postLoginWorker(payload) {
  try {
    yield put(ga.fetchRequest({ text: 'login...' }));
    const authResponse = yield call(postLogin, payload);
    yield put(ga.fetchSuccess());

    //save token to localstorage
    if (authResponse.status === 200 && authResponse.data?.token) {
      // retrieve resource list
      localStorage.setItem('id_token', authResponse.data.token);
      localStorage.setItem('notification-status', true);
      setCookie(COOKIE_NAME, authResponse.data.token, 1);

      const usersModules = yield fetchUsersModulesWorker();
      const selectedProduct = usersModules[0];
      yield put(setCurrentProductLogin(selectedProduct));

      const resourceResponse = yield call(
        fetchResources,
        authResponse.data.token,
        selectedProduct.applicationId,
        selectedProduct.roleCode
      );
      if (resourceResponse.status === 200) {
        const resourceDataList = resourceResponse.data.policies;
        yield put(postLoginSuccess(resourceDataList));
      }
    }
  } catch (error) {
    localStorage.removeItem('id_token');
    eraseCookie(COOKIE_NAME);
  }
}

function* productLoginWorker(payload) {
  try {
    const token = localStorage.getItem('id_token');
    const { applicationId, roleCode } = payload;

    const resourceResponse = yield call(
      fetchResources,
      token,
      applicationId,
      roleCode
    );

    if (resourceResponse.status === 200) {
      yield put(setCurrentProductLogin(payload));

      const resourceDataList = resourceResponse.data.policies;
      yield put(productLoginSuccess(resourceDataList));
      yield put(push('/app'));
    } else {
      throw resourceResponse;
    }
  } catch (error) {
    yield put(ga.fetchFailed());
    yield put(productLoginFail(error));
  }
}

function* postChangePasswordWorker(payload) {
  try {
    yield put(ga.fetchRequest({ text: 'change password...' }));
    const result = yield call(putChangePassword, payload);
    yield put(ga.fetchSuccess());

    yield put(putChangePasswordSuccess(result));
  } catch (error) {
    yield put(ga.fetchFailed());
    yield put(putChangePasswordFail(error));
  }
}

function* putChangeDefaultRoleWorker(payload) {
  try {
    const token = localStorage.getItem('id_token');
    const { roleCode, moduleCode, isDefault } = payload;

    const result = yield call(
      putChangeDefaultRole,
      token,
      roleCode,
      moduleCode,
      isDefault
    );

    if (result.status === 200 && result.data) {
      yield put(fetchUsersModulesRequest());
      yield put(putChangeDefaultRoleSuccess(result));
      if (result?.data?.message) {
        const successMessage = result.data.message.replace(/.*:(.*)\)/, '$1');
        message.success(successMessage);
      }
    } else {
      throw result;
    }
  } catch (error) {
    yield put(putChangeDefaultRoleFail(error));
    if (error.data?.message) {
      message.success(error.data?.message);
    }
  }
}

function* postLogoutWorker(payload) {
  try {
    localStorage.removeItem('id_token');
    localStorage.removeItem('productId');
    eraseCookie(COOKIE_NAME);
    yield put(ga.fetchRequest({ text: 'logout...' }));
    yield call(postLogout, payload);
    yield put(ga.fetchSuccess());
  } catch (error) {
    yield put(ga.fetchFailed());
  }
  yield put(postLogoutDone());
  yield put(replace('/login'));
  location.reload();
}

function* resetLoginWorker(payload) {
  yield put(resetLogin());
}

export function* watchLogin() {
  yield takeEvery(POST_LOGIN_REQUEST, function* ({ payload }) {
    yield fork(postLoginWorker, payload);
  });
  yield takeEvery(PRODUCT_LOGIN_REQUEST, function* ({ payload }) {
    yield fork(productLoginWorker, payload);
  });
}

export function* watchUserModules() {
  yield takeEvery(FETCH_USERS_MODULES_REQUEST, function* ({ payload }) {
    yield fork(fetchUsersModulesWorker, payload);
  });
}

export function* watchChangePassword() {
  yield takeEvery(PUT_CHANGE_PASSWORD_REQUEST, function* ({ payload }) {
    yield fork(postChangePasswordWorker, payload);
  });
}

export function* watchChangeRole() {
  yield takeEvery(PUT_CHANGE_DEFAULT_ROLE_REQUEST, function* ({ payload }) {
    yield fork(putChangeDefaultRoleWorker, payload);
  });
}

export function* watchLogout() {
  yield takeEvery(POST_LOGOUT_REQUEST, function* ({ payload }) {
    yield fork(postLogoutWorker, { payload });
  });
}

export function* watchResetLogout() {
  yield fork(resetLoginWorker);
}
